import { useState, useEffect, useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import { copyAllProductTemplates } from '@/api/admin'
import { Dropdown, FullscreenModal } from '@/components/global'
import Flex from '@/components/priority/Flex/Flex'
import { Button } from '@/components/priority'
import Text from '@/components/priority/Text/Text'
import { getAllProducts } from '@/api/products'
import { getThemeCategories } from '@/api/themes'
import { getCustomPageSets } from '@/api/pageSets'
import { applicationTypeAtom } from '@/atoms'
import { getErrorMessage } from '@/utils/global/getErrorMessage'
import { LaunchCopyOutcome } from './LaunchCopyOutcome'

const ALL_OPTION_ID = '__ALL__'

export const ProductLayoutCopyModal = ({ show }) => {
  const close = show[1]
  const applicationType = useRecoilValue(applicationTypeAtom)
  const [products, setProducts] = useState([])
  const [sourceProduct, setSourceProduct] = useState('')
  const [targetProduct, setTargetProduct] = useState('')
  const [sourceThemes, setSourceThemes] = useState([])
  const [sourceTheme, setSourceTheme] = useState(ALL_OPTION_ID)
  const [sourceLayouts, setSourceLayouts] = useState([])
  const [sourceLayout, setSourceLayout] = useState(ALL_OPTION_ID)
  const [loading, setLoading] = useState(false)
  const [refusal, setRefusal] = useState(null)
  const [skipped, setSkipped] = useState([])

  const resetState = () => {
    setRefusal(null)
    setSkipped([])
    setSourceProduct('')
    setTargetProduct('')
    setSourceTheme(ALL_OPTION_ID)
    setSourceLayout(ALL_OPTION_ID)
    setSourceThemes([])
    setSourceLayouts([])
  }

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getAllProducts()
        const sortedProducts = response.data.sort((a, b) =>
          a.name.localeCompare(b.name),
        )
        setProducts(sortedProducts)
      } catch (error) {
        console.error('Failed to fetch products:', error)
      }
    }

    fetchProducts()
  }, [])

  useEffect(() => {
    setSourceTheme(ALL_OPTION_ID)
    setSourceLayout(ALL_OPTION_ID)
    setSourceLayouts([])

    if (!sourceProduct) {
      setSourceThemes([])
      return
    }

    const fetchThemes = async () => {
      try {
        const response = await getThemeCategories({ productId: sourceProduct })
        const sorted = response.data[0].themes.sort((a, b) =>
          a.name.localeCompare(b.name),
        )
        setSourceThemes(sorted)
      } catch (error) {
        console.error('Failed to fetch source themes:', error)
      }
    }

    fetchThemes()
  }, [sourceProduct])

  useEffect(() => {
    setSourceLayout(ALL_OPTION_ID)

    if (!sourceProduct || !sourceTheme || sourceTheme === ALL_OPTION_ID) {
      setSourceLayouts([])
      return
    }

    const fetchLayouts = async () => {
      try {
        const response = await getCustomPageSets(
          sourceProduct,
          sourceTheme,
          undefined,
          applicationType,
        )
        const sorted = response.data.sort((a, b) =>
          a.name.localeCompare(b.name),
        )
        setSourceLayouts(sorted)
      } catch (error) {
        console.error('Failed to fetch source layouts:', error)
      }
    }

    fetchLayouts()
  }, [sourceProduct, sourceTheme, applicationType])

  const themeItems = useMemo(
    () => [{ id: ALL_OPTION_ID, name: 'All themes' }, ...sourceThemes],
    [sourceThemes],
  )

  const layoutItems = useMemo(
    () => [{ id: ALL_OPTION_ID, name: 'All layouts' }, ...sourceLayouts],
    [sourceLayouts],
  )

  const handleSubmit = async () => {
    if (!sourceProduct || !targetProduct) return
    if (sourceProduct === targetProduct) return

    setLoading(true)
    setRefusal(null)
    setSkipped([])
    try {
      const payload = {
        sourceProductId: sourceProduct,
        targetProductId: targetProduct,
      }
      if (sourceTheme && sourceTheme !== ALL_OPTION_ID) {
        payload.sourceThemeId = sourceTheme
      }
      if (sourceLayout && sourceLayout !== ALL_OPTION_ID) {
        payload.sourcePageSetIds = [sourceLayout]
      }

      const response = await copyAllProductTemplates(payload)
      const left = response.data.skipped ?? []
      if (left.length > 0) {
        setSkipped(left)
        return
      }
      resetState()
      close()
    } catch (error) {
      setRefusal(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    resetState()
    close()
  }

  const sameProductSelected =
    !!sourceProduct && !!targetProduct && sourceProduct === targetProduct

  const isDisabled =
    !sourceProduct || !targetProduct || sameProductSelected || loading

  return (
    <FullscreenModal show={show} hideClose>
      <Flex
        column
        padding="32px"
        width="500px"
        maxHeight="90vh"
        justify="space-between"
      >
        <Flex column>
          <Text size="24px" weight="600" margin="0 0 20px 0">
            Copy Product Layouts
          </Text>
          <Text margin="0 0 20px 0">
            Copy layouts from a source product to a target product. Optionally
            narrow to a specific source theme and a single source layout. Each
            chosen template is applied across all themes of the target product
            (matching names are overwritten; missing names are created).
          </Text>
          <Text margin="0 0 20px 0" color="red">
            This will permanently override any existing target product layouts
            with similar names.
          </Text>

          <Text margin="0 0 6px 0" weight="600">
            Source product
          </Text>
          <Dropdown
            width="100%"
            margin="0 0 16px 0"
            color="inherit"
            data-testid="product-copy-source"
            selected={[sourceProduct, setSourceProduct]}
            placeholder="Select source product (copy FROM)"
            items={products}
            valueKey="id"
            textKey="name"
            full={true}
          />

          {sourceProduct && (
            <>
              <Text margin="0 0 6px 0" weight="600">
                Source theme (optional)
              </Text>
              <Dropdown
                width="100%"
                margin="0 0 16px 0"
                color="inherit"
                selected={[sourceTheme, setSourceTheme]}
                placeholder="All themes"
                items={themeItems}
                valueKey="id"
                textKey="name"
                full={true}
              />
            </>
          )}

          {sourceProduct && sourceTheme && sourceTheme !== ALL_OPTION_ID && (
            <>
              <Text margin="0 0 6px 0" weight="600">
                Source layout (optional)
              </Text>
              <Dropdown
                width="100%"
                margin="0 0 16px 0"
                color="inherit"
                selected={[sourceLayout, setSourceLayout]}
                placeholder="All layouts in this theme"
                items={layoutItems}
                valueKey="id"
                textKey="name"
                full={true}
              />
            </>
          )}

          <Text margin="0 0 6px 0" weight="600">
            Target product
          </Text>
          <Dropdown
            width="100%"
            margin="0 0 16px 0"
            color="inherit"
            data-testid="product-copy-target"
            selected={[targetProduct, setTargetProduct]}
            placeholder="Select target product (copy TO)"
            items={products}
            valueKey="id"
            textKey="name"
            full={true}
          />

          {sameProductSelected && (
            <Text margin="0 0 12px 0" color="red">
              Source and target product must be different.
            </Text>
          )}
          <LaunchCopyOutcome refusal={refusal} skipped={skipped} />
        </Flex>
        <Flex justify="flex-end" margin="24px 0 0 0">
          <Button margin="0 16px 0 0" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            primary
            left
            margin="0 12px 0 0"
            onClick={handleSubmit}
            disabled={isDisabled}
            opacity={isDisabled ? 0.5 : 1}
            pointerEvents={isDisabled ? 'none' : 'auto'}
          >
            {loading ? 'Copying...' : 'Copy layouts to target product'}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}
